import { useEffect, useMemo } from 'react';
import SchemaSipProfileService from '@frontend/api-sip-profile';
import { SoftphoneQueries } from '@frontend/api-softphone';
import { useAppSettingsShallowStore } from '@frontend/app-settings-store';
import { http } from '@frontend/fetch';
import { useQuery } from '@frontend/react-query-helpers';

export const useSoftphoneSettingsQuery = ({
  userAgent,
  tenantId,
  sipProfileId,
  enabled,
}: {
  userAgent: string;
  tenantId: string;
  sipProfileId: string;
  enabled: boolean;
}) => {
  const {
    data: settings,
    refetch: refetchSettings,
    isFetching,
  } = SoftphoneQueries.useGetCurrentUserSoftphoneQuery({
    req: { userAgent },
    opts: {
      enabled,
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      tenantId,
    },
  });

  const { softphone, sipProfile } = useMemo(() => {
    const softphone = settings?.softphones.find((softphone) => {
      return softphone.sipProfiles.some((sipProfile) => {
        return sipProfile.id === sipProfileId;
      });
    });
    return {
      softphone,
      sipProfile: softphone?.sipProfiles.find((sipProfile) => sipProfile.id === sipProfileId),
    };
  }, [settings, sipProfileId]);

  return {
    settings,
    softphone,
    sipProfile,
    refetchSettings,
    isFetching,
  };
};

export const useSoftphoneRegistrationQuery = ({
  tenantId,
  sipProfileId,
  enabled,
}: {
  tenantId: string;
  sipProfileId: string;
  enabled: boolean;
  lastRegisteredCallId: string;
}) => {
  const { data, error, refetch, isRefetching, isFetching } = useQuery({
    queryKey: [tenantId, sipProfileId, 'softphone-registration'],
    queryFn: () =>
      SchemaSipProfileService.GetRegistration({
        sipProfileId,
      }),
    enabled,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    staleTime: 0,
  });

  const { setSoftphoneRegistrationExists } = useAppSettingsShallowStore('setSoftphoneRegistrationExists');

  useEffect(() => {
    /**
     * If registration.active is true, that means the softphone is registered _somewhere_.
     * It could be in another tab, or it could be in another browser, but not in this instance of the app.
     */
    setSoftphoneRegistrationExists((data && data.registration.active) ?? false);
  }, [data, setSoftphoneRegistrationExists]);

  const isRegistrationAvailable = data && !data.registration.active;
  const firstTimeRegistering = http.isHttpError(error) && error.status === 404;
  const isRegistrationAllowed = isRegistrationAvailable || firstTimeRegistering;

  return useMemo(
    () => ({
      isRegistrationStatusFetching: isRefetching || isFetching,
      isRegistrationAllowed,
      refetchRegistrationStatus: refetch,
      firstTimeRegistering,
      registration: data?.registration,
    }),
    [isRefetching, isFetching, isRegistrationAllowed, refetch, firstTimeRegistering, data?.registration]
  );
};
